<template>
  <b-modal
    :id="`mc-modal-bulk-complete-${point.id}`"
    ref="mc-modal-complete-v2"
    class="mc-modal"
    :title="title"
    size="xl"
    centered
    scrollable
    hide-header-close
    no-close-on-esc
    no-close-on-backdrop
    @show="handleModalShow"
  >
    <div v-if="!isComplete" class="card-body p-2">
      <McWizard
        v-if="showDeliveryLoadsStep"
        compact
        :items="wizardItems"
        :activeKey="activeStep"
        :withNumber="true"
        class="mc-wizard mb-3"
      />

      <McAlert
        v-if="!isSingleComplete"
        class="mb-3"
        variant="info"
        :html="
          $t('fleetTask.bulkCompleteActivityModal.alert.bulkProofSaved', {
            activityLabel: activityLabelLower,
            locationName: point.address.name,
          })
        "
        withIcon
        :withGap="false"
      />
      <p v-if="activeStep === BULK_COMPLETE_ACTIVITY_STEPS.FORM" class="label__do-number ms-1 mb-1">
        {{ proofLabel }}
      </p>

      <FormulateForm
        v-show="!showDeliveryLoadsStep || activeStep === BULK_COMPLETE_ACTIVITY_STEPS.FORM"
        ref="formRef"
        v-model="formValues"
        class="mc-form"
        :errors="formErrors"
      >
        <b-row>
          <b-col md="6">
            <FormulateInput
              type="text"
              validation="required"
              name="contact_person_name"
              :help="true"
              :label="contactPersonLabel"
              :placeholder="contactPersonPlaceholder"
              :disabled="isLoading"
            />
          </b-col>
          <b-col md="6" class="bulk-complete-date-field">
            <FormulateInput
              type="date-picker"
              validation="bail|required|customDateValidation"
              format="DD MMM YYYY HH:mm"
              datepickerType="datetime"
              valueType="YYYY-MM-DDTHH:mm:ss.SSSZ"
              :confirmText="$t('global.action.apply')"
              name="completed_on"
              :validation-rules="{ customDateValidation }"
              :validation-messages="{
                customDateValidation: customDateValidationMessage,
              }"
              :help="true"
              :confirm="true"
              :showSecond="false"
              :label="completedOnLabel"
              :placeholder="completedOnPlaceholder"
              :disabled="isLoading"
              :disabled-date="isCompletedOnDisabledDate"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <BulkAttachmentField
              :activities="pointActivities"
              :fleetTaskId="fleetTaskId"
              :pointId="point.id"
              :attachments="attachments"
              :isLoading="isLoading"
              :isSingleComplete="isSingleComplete"
              :selectedActivity="selectedActivity"
              @update:isLoading="isLoading = $event"
              @add-attachment="handleAddAttachment"
            />
          </b-col>

          <b-col md="6">
            <FormulateInput
              type="textarea"
              name="note"
              :label="$t('fleetTask.bulkCompleteActivityModal.fields.note')"
              class="note-textarea"
              :placeholder="$t('fleetTask.bulkCompleteActivityModal.placeholders.note')"
              :disabled="isLoading"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <!-- catatan tambahan -->
            <FormulateInput
              id="customer_notes"
              ref="customer_notes-ref"
              v-model="customerNotes"
              name="customer_notes"
              class="mt-2"
              :help="true"
              :label="$t('global.field.additionalNote')"
              :placeholder="$t('fleetTask.placeholder.additionalNote')"
              trackBy="id"
              displayedBy="note"
              :multiple="!hasCustomFeatureSamator"
               :validation="isCustomerNotesRequired ? 'bail|customerNotesRequiredValidation' : ''"
              :validation-rules="{ customerNotesRequiredValidation }"
              :validation-messages="{
                customerNotesRequiredValidation: $t('global.validation.required'),
              }"
              type="select-resourceful-v3"
              :optionsGetter="resourcefulQuery.customerNotes"
              :optionLabelFn="({ item }) => item.note"
              :fullObject="fullObject.customerNotes"
              selectedLabel="note"
              :hasClose="false"
              :visibleTagCount="3"
              :hasAttachment="true"
              attachmentKey="document_attachment_url"
              :disabled="canAddCustomerNote"
              @update:fullObject="fullObject.customerNotes = $event"
            />
          </b-col>
        </b-row>
      </FormulateForm>
    </div>

    <DeliveryLoads
      v-show="showDeliveryLoadsStep && activeStep === BULK_COMPLETE_ACTIVITY_STEPS.DELIVERY_LOADS"
      ref="deliveryLoadComponent"
      :fleetTaskId="fleetTaskId"
      :customerNotes="customerNotes"
      :customerNotesThirdPartyId="customerNotesThirdPartyId"
      :activity="activity"
      :activities="pointActivities"
      :isLoading="isLoading"
      :isPickUp="isPickUp"
      :isComplete="isComplete"
      :isCompletedPickUp="isCompletedPickUp"
      :isCompletedDrop="isCompletedDrop"
      :isAdjustedActivity="isAdjustedActivity"
      :isSingleComplete="isSingleComplete"
      :selectedActivity="selectedActivity"
      @update:adjusted-loads="handleAdjustedLoads"
      @update:converted="handleConverted"
      @update:has-errors="hasDeliveryLoadErrors = $event"
    />

    <template #modal-footer>
      <div class="d-flex mx-0">
        <McButton
          variant="secondary"
          class="me-2"
          :text="$t('global.cancel')"
          @click="handleModalCancel"
        />
        <McButton
          v-if="showDeliveryLoadsStep && activeStep === BULK_COMPLETE_ACTIVITY_STEPS.DELIVERY_LOADS"
          variant="secondary"
          class="me-2"
          :text="$t('global.prev')"
          @click="goToPrevStep"
        />
        <McButton
          v-if="showDeliveryLoadsStep && activeStep === BULK_COMPLETE_ACTIVITY_STEPS.FORM"
          variant="primary"
          :disabled="isNextStepDisabled"
          :text="$t('global.next')"
          @click="goToNextStep"
        />

        <McButton
          v-else-if="!(isCompletedDrop && isCompletedPickUp && isPickUp)"
          :disabled="
            activeStep === BULK_COMPLETE_ACTIVITY_STEPS.DELIVERY_LOADS
              ? (isContainLiquid && !isPickUp)
                ? isSingleComplete ? convertedDeliveries.length === 0 : 
                  hasDeliveryLoadErrors || (!allLiquidConverted) 
                : (!hasChanged || hasDeliveryLoadErrors)
              : (!hasChanged || !isFormStepValid)
          "
          variant="primary"
          :text="
            isComplete
              ? $t('fleetTask.bulkCompleteActivityModal.actions.saveChanges')
              : $t('fleetTask.bulkCompleteActivityModal.actions.confirmComplete')
          "
          @click="isComplete ? handleUpdate() : handleComplete()"
        />
      </div>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import McAlert from '@/components/McAlert'
import McWizard from '@/components/McWizard'
import * as FleetTaskActivity from '@/services/rest-api/lib/fleet-task/activity'
import * as DeliveryNote from '@/services/rest-api/lib/customer/delivery-note'
import BulkAttachmentField from './BulkAttachmentField'
import { EventBus } from '@/router/views/fleet-task/lib/event-bus'
import { mapState, mapGetters } from 'vuex'
import { MixPanel } from '@/mixin/mixpanel'
import { Helper } from '@/mixin/helper'

import getRelatedActivity from '../lib/get-related-activity'
import apiErrorMappings from '../lib/constants/api-error-mappings'
import {
  errorCodeInputMap,
  errorCodeInputMapFindFieldKey,
  errorCodeToastMap,
} from '../lib/constants/error-code-mappings'
import DeliveryLoads from './DeliveryLoads'

import moment from 'moment'
moment.locale('id')

export const BULK_COMPLETE_ACTIVITY_STEPS = Object.freeze({
  FORM: 'form',
  DELIVERY_LOADS: 'delivery-loads',
})

export default {
  components: {
    McButton,
    BulkAttachmentField,
    DeliveryLoads,
    McAlert,
    McWizard,
  },
  mixins: [MixPanel, Helper],
  props: {
    point: {
      type: Object,
      default: () => {},
    },
    fleetTaskId: {
      type: String,
      default: '',
    },
    isSingleComplete: {
      type: Boolean,
      default: false,
    },
    selectedActivity: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      isLoading: false,

      formValues: {},
      formErrors: {},
      invalidFormValues: {},

      attachments: [],
      adjustedDeliveryLoads: [],
      customerNotes: [],
      fullObject: {
        customerNotes: [],
      },
      BULK_COMPLETE_ACTIVITY_STEPS: BULK_COMPLETE_ACTIVITY_STEPS,
      activity: {},
      storeActivityIds: [],
      hasDeliveryLoadErrors: false,
      activeStep: BULK_COMPLETE_ACTIVITY_STEPS.FORM,
      convertedDeliveries: [],
    }
  },
  computed: {
    ...mapState('fleetTask', ['sequence', 'fleetTaskDetail', 'selectedDos']),
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    customerNotesThirdPartyId(){
      return this.fullObject?.customerNotes?.third_party_id
    },
    allLiquidConverted() {
      return this.pointActivities.filter(
        p=>p.delivery_order.user_definition_fields.some(
          obj => obj.name?.toLowerCase() === "product type" && ['tube', 'bulk'].includes(obj.value?.toLowerCase())
        )
      ).length === this.convertedDeliveries.length
    },
    isContainLiquid() {
      const filtered = this.pointActivities.filter((act) => {
        const doData = act?.delivery_order
        return (doData?.load_count || 0) > 0 && !doData?.is_from_vendor_request
      })

      if (filtered.length > 0) {
        const hasLiquid = filtered.some((act) => {
          const productTypeField = act.delivery_order?.user_definition_fields?.find(
            (field) => field?.name === 'Product Type'
          )
          const productTypeValue = (productTypeField?.value || '').toString().trim().toLowerCase()

          return ['bulk', 'tube'].includes(productTypeValue)
        })
        return hasLiquid
      }

      return false
    },
    title() {
      if (this.isSingleComplete) {
        return this.$t('fleetTask.bulkCompleteActivityModal.title.completeSingle', {
          activityLabel: this.activityLabel,
          deliveryNumber: this.selectedActivity?.delivery_order?.number,
        })
      }
      return this.$t('fleetTask.bulkCompleteActivityModal.title.completeBulk', {
        activityLabel: this.activityLabel,
        count: this.point?.activities?.length,
      })
    },
    deliveryOrderDetails() {
      const targetId = this.activity?.delivery_order?.id
      return this.selectedDos.find((doNumber) => doNumber.id === targetId) || {}
    },
    isPickUp() {
      if (this.isSingleComplete) {
        return this.selectedActivity?.type === 'PICKUP'
      }
      const firstType = this.point?.activities?.[0]?.type
      const hasSameType = this.point?.activities?.every((a) => a.type === firstType)
      return hasSameType && firstType === 'PICKUP'
    },
    isComplete() {
      return false
    },
    isCompletedPickUp() {
      const activity = this.resolveActiveActivity()
      const targetId = activity?.delivery_order?.id
      const defaultPoints = this.sequence.filter((point) => point.type === 'DEFAULT')
      const found = defaultPoints.some((point) => {
        return point.activities.some(
          (activity) =>
            activity.type === 'PICKUP' &&
            activity.delivery_order.id === targetId &&
            activity.status.raw_type === 'COMPLETED'
        )
      })

      return found
    },
    isCompletedDrop() {
      const activity = this.resolveActiveActivity()
      const targetId = activity?.delivery_order?.id
      const defaultPoints = this.sequence.filter((point) => point.type === 'DEFAULT')
      const found = defaultPoints.some((point) => {
        return point.activities.some(
          (activity) =>
            activity.type === 'DROP' &&
            activity.delivery_order.id === targetId &&
            activity.status.raw_type === 'COMPLETED'
        )
      })
      return found
    },
    isAdjustedActivity() {
      return (this.isSingleComplete? this.deliveryOrderDetails.is_load_adjusted : this.activitiesDeliveryOrderDetails.some((item) => item.is_load_adjusted ))
    },
    pointActivities() {
      return this.point?.activities || []
    },
    activitiesDeliveryOrderIds() {
      return this.pointActivities.map((item) => item.delivery_order.id)
    },
    activitiesDeliveryOrderDetails() {
      const idSet = new Set(this.activitiesDeliveryOrderIds)
      return this.selectedDos.filter((doItem) => idSet.has(doItem.id))
    },
    enabledAdjustment() {
      if (this.isSingleComplete) {
        const odId = this.selectedActivity?.delivery_order?.id
        const deliveryOrderDetail = this.activitiesDeliveryOrderDetails.find(
          (item) => item.id === odId
        )
        const deliveryOrderDetailOrigin = deliveryOrderDetail?.origin_address?.id
        const deliveryOrderDetailDestination = deliveryOrderDetail?.destination_address?.id

        const sequenceDetail = this.sequence.find((point) => point.id === this.point.id)
        const sequenceDetailAddress = sequenceDetail?.address?.id

        const hasLoad = deliveryOrderDetail?.load_count > 0 ? true : false

        if (deliveryOrderDetail.is_from_vendor_request) {
          return false
        } else if (deliveryOrderDetail?.shipment_type === 'DIRECT') {
          if (
            this.hasCustomFeatureSamator &&
            this.selectedActivity.type === 'PICKUP' &&
            this.isProductTypeLiquid(deliveryOrderDetail)
          ) {
            return false
          }
          if (!hasLoad) return false
          return true
        } else if (deliveryOrderDetail?.shipment_type === 'TRANSIT') {
          if (this.activity.type === 'PICKUP') {
            if (this.hasCustomFeatureSamator && this.isProductTypeLiquid(deliveryOrderDetail)) {
              return false
            }
            if (deliveryOrderDetailOrigin === sequenceDetailAddress) {
              if (!hasLoad) return false
              return true
            }
          } else if (this.activity.type === 'DROP') {
            if (deliveryOrderDetailDestination === sequenceDetailAddress) {
              if (!hasLoad) return false
              return true
            }
          }
          return false
        }
        return false
      } else {
        const doIsFromVendorRequest = this.activitiesDeliveryOrderDetails.some(
          (item) => item.is_from_vendor_request
        )
        const doIsDirectAndHasLoad = this.activitiesDeliveryOrderDetails.some(
          (item) => item.shipment_type === 'DIRECT' && item.load_count > 0
        )
        const hasBulkOrTubeProductType = this.activitiesDeliveryOrderDetails.some((item) =>
          this.isProductTypeLiquid(item)
        )

        const isAllActivityPickup = this.pointActivities.every((a) => a.type === 'PICKUP')
        if (this.hasCustomFeatureSamator && isAllActivityPickup && hasBulkOrTubeProductType) {
          return false
        }

        if (doIsFromVendorRequest) return false
        if (doIsDirectAndHasLoad) return true
      }

      return false
    },
    showDeliveryLoadsStep() {
      return (
        this.hasAccessAll('delivery-load-adjustment:read', 'delivery-load-adjustment:update') &&
        this.enabledAdjustment
      )
    },
    wizardItems() {
      return [
        {
          key: BULK_COMPLETE_ACTIVITY_STEPS.FORM,
          label: this.$t('fleetTask.bulkCompleteActivityModal.steps.completeProof'),
        },
        {
          key: BULK_COMPLETE_ACTIVITY_STEPS.DELIVERY_LOADS,
          label: this.$t('fleetTask.bulkCompleteActivityModal.steps.reviewLoad'),
        },
      ]
    },
    hasChanged() {
      if (this.isLoading) return false

      let currentFormValues = this.formValues

      const [_, hasChanged] = this.filterUnchangedData(currentFormValues, {})

      const hasAdjustedLoads = this.adjustedDeliveryLoads.length > 0

      return hasChanged || hasAdjustedLoads
    },
    customDateValidationMessage() {
      return this.isPickUp
        ? this.$t('fleetTask.bulkCompleteActivityModal.validation.pickupTimeRange')
        : this.$t('fleetTask.bulkCompleteActivityModal.validation.deliveryTimeRange')
    },
    isNextStepDisabled() {
      if (this.isLoading) return true

      return !this.isFormStepValid
    },
    isFormStepValid() {
      const contactPersonName = this.formValues?.contact_person_name
      const completedOn = this.formValues?.completed_on
      const isCompletedOnValid = completedOn
        ? this.customDateValidation({ value: completedOn })
        : false
      const hasContactPersonName =
        typeof contactPersonName === 'string'
          ? contactPersonName.trim().length > 0
          : Boolean(contactPersonName)
      const hasRequiredCustomerNotes = !this.isCustomerNotesRequired
        ? true
        : this.normalizeCustomerNoteUuids().length > 0

      return hasContactPersonName && isCompletedOnValid && hasRequiredCustomerNotes
    },
    isCustomerNotesRequired() {
      return this.hasCustomFeatureSamator && this.isContainLiquid && !this.isPickUp
    },
    relatedActivity() {
      return getRelatedActivity(this.activity, this.sequence)
    },
    invalidFieldKeys() {
      return Object.keys(this.invalidFormValues)
    },
    resourcefulQuery() {
      const deliveryOrderId = this.isSingleComplete
        ? this.selectedActivity?.delivery_order?.id
        : this.point?.activities?.[0]?.delivery_order?.id

      const customerId = this.selectedDos.find((doNumber) => doNumber.id === deliveryOrderId)
        ?.customer?.id
      return {
        customerNotes: async (opts = {}) => {
          if (!customerId) {
            return {
              data: { paginated_result: [] },
              metadata: { total_page: 1, total_count: 0, page: 1 },
            }
          }

          const getter = DeliveryNote.get(customerId)
          return await getter(opts)
        },
      }
    },
    canAddCustomerNote() {
      if (this.isSingleComplete) return false

      const activities = this.point?.activities || []
      if (activities.length <= 1) return false

      const firstCustomer = activities[0]?.delivery_order?.customer?.id
      const isSameCustomer = activities.every(
        (a) => a?.delivery_order?.customer?.id === firstCustomer
      )
      if (isSameCustomer) return false

      return true
    },
    hasCustomFeatureSamator() {
      const data = this.getFeatureFlagValue('customFeature.samator')
      return Array.isArray(data?.company_id) && data.company_id.includes(this.getCompanyId())
    },
    activityLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.activity.pickup'
          : 'fleetTask.bulkCompleteActivityModal.activity.delivery'
      )
    },
    activityLabelLower() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.activity.pickupLower'
          : 'fleetTask.bulkCompleteActivityModal.activity.deliveryLower'
      )
    },
    activitySuccessLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.activity.pickedUp'
          : 'fleetTask.bulkCompleteActivityModal.activity.delivered'
      )
    },
    proofLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.labels.pickupProof'
          : 'fleetTask.bulkCompleteActivityModal.labels.deliveryProof'
      )
    },
    contactPersonLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.fields.senderName'
          : 'fleetTask.bulkCompleteActivityModal.fields.recipientName'
      )
    },
    contactPersonPlaceholder() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.placeholders.senderName'
          : 'fleetTask.bulkCompleteActivityModal.placeholders.recipientName'
      )
    },
    completedOnLabel() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.fields.pickupDate'
          : 'fleetTask.bulkCompleteActivityModal.fields.deliveryDate'
      )
    },
    completedOnPlaceholder() {
      return this.$t(
        this.isPickUp
          ? 'fleetTask.bulkCompleteActivityModal.placeholders.pickupDate'
          : 'fleetTask.bulkCompleteActivityModal.placeholders.deliveryDate'
      )
    },
  },
  watch: {
    async formValues() {
      if (this.invalidFieldKeys.length === 0) return

      this.invalidFieldKeys.forEach((fieldErrorKey) => {
        if (this.invalidFormValues[fieldErrorKey] != this.formValues[fieldErrorKey]) {
          // delete the field from the state to show errors
          this.$delete(this.formErrors, fieldErrorKey)
          this.$delete(this.invalidFormValues, fieldErrorKey)
        }
      })

      // empty the invalidFormValues if all error key is deleted
      if (this.invalidFieldKeys.length === 0) {
        this.invalidFormValues = {}
      }
    },
    customerNotes() {
      const hasValue = this.normalizeCustomerNoteUuids().length > 0
      if (!hasValue) return

      this.$delete(this.formErrors, 'customer_notes')
      this.$delete(this.invalidFormValues, 'customer_notes')
    },
  },
  methods: {
    isCompletedOnDisabledDate(date) {
      const selectedDate = moment(date)
      if (!selectedDate.isValid()) return false

      const now = moment()
      const baseTime = this.isPickUp
        ? this.fleetTaskDetail?.actual_departure_on
        : this.relatedActivity?.completed_on

      let res
      if (selectedDate.isAfter(now)){
        res = true
      }else if (!baseTime) {
        res = false
      } else{
        res = selectedDate.isBefore(moment(baseTime).subtract(1, 'day'))
      }

      return res
    },
    resolveActiveActivity() {
      return Object.keys(this.selectedActivity ?? {}).length > 0
        ? this.selectedActivity
        : this.pointActivities[0] ?? {}
    },
    HandleCompleteActivity() {
      this.activity = this.resolveActiveActivity()

      if (!this.isPickUp && !this.relatedActivity.completed_on) {
        this.$mcToast.error(this.$t('fleetTask.bulkCompleteActivityModal.toast.pickupMustBeCompleted'))
        return
      }

      this.$bvModal.show(`mc-modal-complete-v2-${this.activity.id}`)
    },
    customDateValidation(context) {
      const value = context?.value ?? context
      const dt = moment(value)
      const baseTime = this.isPickUp
        ? this.fleetTaskDetail?.actual_departure_on
        : this.relatedActivity?.completed_on

      if (!value || !dt.isValid()) return false
      if (dt.isAfter(moment())) return false
      if (!baseTime) return true

      const res = dt.isSameOrAfter(moment(baseTime))
      return res
    },
    customerNotesRequiredValidation() {
      const normalized = this.normalizeCustomerNoteUuids()
      const isValid = normalized.length > 0
      return isValid
    },
    isProductTypeLiquid(deliveryOrderDetail = {}) {
      const productTypeField = deliveryOrderDetail?.user_definition_fields?.find(
        (field) => field?.name === 'Product Type'
      )
      const productTypeValue = (productTypeField?.value || '').toString().trim().toLowerCase()

      return ['bulk', 'tube'].includes(productTypeValue)
    },
    handleAdjustedLoads(loads) {
      this.adjustedDeliveryLoads = Array.isArray(loads) ? loads.map((load) => ({ ...load })) : []
    },
    handleConverted(delivery_id, is_converted) {
      if (is_converted && !this.convertedDeliveries.includes(delivery_id)) {
        this.convertedDeliveries.push(delivery_id)
      } else if (!is_converted) {
        const index = this.convertedDeliveries.indexOf(delivery_id)
        if (index > -1) {
          this.convertedDeliveries.splice(index, 1)
        }
      }
    },
    normalizeCustomerNoteUuids() {
      if (Array.isArray(this.customerNotes)) {
        return this.customerNotes.filter(Boolean)
      }
      if (this.customerNotes == null || this.customerNotes === '') {
        return []
      }
      return [this.customerNotes]
    },
    goToNextStep() {
      if (!this.showDeliveryLoadsStep || this.isNextStepDisabled || this.$refs.formRef?.hasErrors)
        return

      this.activeStep = BULK_COMPLETE_ACTIVITY_STEPS.DELIVERY_LOADS
    },
    goToPrevStep() {
      this.activeStep = BULK_COMPLETE_ACTIVITY_STEPS.FORM
    },
    async handleComplete() {
      this.isLoading = true

      if (this.$refs.formRef?.hasErrors) {
        this.$refs.formRef?.showErrors()
        this.$mcToast.error(
          this.$t('fleetTask.bulkCompleteActivityModal.toast.failedToComplete', {
            activityLabel: this.activityLabelLower,
          })
        )
        this.isLoading = false
        return
      }

      const activity_ids = this.isSingleComplete
        ? [this.selectedActivity?.id]
        : this.pointActivities.map((a) => a.id)

      this.storeActivityIds = activity_ids

      const data = {
        completion_source: 'web',
        queues: [
          {
            type: 'complete',
            point_id: this.point.id,
            activity_ids: activity_ids,
            payload: {
              ...this.formValues,
            },
          },
        ],
      }

      if (this.adjustedDeliveryLoads.length > 0) {
        data.queues[0].payload.adjusted_delivery_loads = this.adjustedDeliveryLoads
      }

      const customerNoteUuids = this.normalizeCustomerNoteUuids()

      if (customerNoteUuids.length > 0) {
        const payload = data.queues[0].payload

        delete payload.customer_notes
        delete payload.customer_note_uuids

        const key = 'customer_note_uuids'

        payload[key] = customerNoteUuids
      }

      const res = await FleetTaskActivity.bulkActivityCompletion(this.fleetTaskId, data)

      const issues = res.errors_v2?.issues

      if (Array.isArray(issues) && issues.length > 0) {
        const targetCodes = [
          'FLEET_TASK.FLEET_TASK_ACTIVITIES_NOT_COMPLETED',
          'FLEET_TASK.DELIVERY_ORDER_INVALID_STATUS',
          'FLEET_TASK.FLEET_TASK_ACTIVITIES_PARENT_NOT_COMPLETED',
        ]

        const hasRelevantIssue = issues.some((issue) => targetCodes.includes(issue.code))

        if (hasRelevantIssue) {
          const index = Number(issues?.[0]?.path?.[3])
          const activity_id = this.storeActivityIds[index]
          let doNumber = this.pointActivities.find((item) => item.id === activity_id)
            ?.delivery_order?.number

          if (this.isSingleComplete) {
            doNumber = this.selectedActivity?.delivery_order?.number
          }
          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTask.bulkCompleteActivityModal.toast.transitInOtherFleetTask', {
                    doNumber,
                  }),
                },
              ],
            },
          })
          this.isLoading = false
          this.$bvModal.hide(`mc-modal-bulk-complete-${this.point.id}`)
          return
        } else {
          const issueCode = issues[0].code
          let fieldKey = issues[0].path[0]
          const findErrorMapping = errorCodeInputMap[issueCode]
          fieldKey = errorCodeInputMapFindFieldKey[issueCode] || fieldKey
          if (findErrorMapping) {
            this.$set(this.formErrors, fieldKey, findErrorMapping)
            this.isLoading = false
            if ([
              "contact_person_name",
              "completed_on",
              "note",
              "customer_notes",
            ].includes(fieldKey)
            ) {
              this.activeStep = BULK_COMPLETE_ACTIVITY_STEPS.FORM
            }
            return
          } else if (fieldKey === 'queues') {
            const toastMessage =
              errorCodeToastMap[issueCode] ||
              this.$t('fleetTask.bulkCompleteActivityModal.toast.failedToChangeStatus')
            this.$mcToast.error(toastMessage)
            this.isLoading = false
            this.$bvModal.hide(`mc-modal-bulk-complete-${this.point.id}`)
            return
          } else {
            if (
              res.errors &&
              res.status == 400 &&
              res.message.includes('KMDI Anji API response error')
            ) {
              this.$mcToast.error(res.message)
              this.isLoading = false
              return
            }

            this.assignErrors(res.errors)
            this.isLoading = false
            return
          }
        }
      }

      if (res.errors && res.status == 400) {
        this.assignErrors(res.errors)
        this.isLoading = false
        return
      }

      this.isLoading = false
      this.$bvModal.hide(`mc-modal-bulk-complete-${this.point.id}`)

      let content = this.$t('fleetTask.bulkCompleteActivityModal.toast.bulkSuccess', {
        count: this.pointActivities.length,
        status: this.activitySuccessLabel,
      })

      if (this.isSingleComplete) {
        content = this.$t('fleetTask.bulkCompleteActivityModal.toast.singleSuccess', {
          doNumber: this.selectedActivity?.delivery_order?.number,
          status: this.activitySuccessLabel,
        })
      }

      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: content,
            },
          ],
        },
      })

      if (this.adjustedDeliveryLoads.length > 0) {
        this.track('Delivery Load Adjustment on Transition', {
          'Fleet Order Number': this.fleetTaskDetail.number,
          'Activity Name': this.isPickUp ? 'Ambil' : 'Kirim',
        })
      }

      this.adjustedDeliveryLoads = []
      EventBus.$emit('refetch-sequence')
    },
    handleAddAttachment(attachment) {
      this.attachments.push(attachment)
    },
    async handleModalShow() {
      this.activeStep = BULK_COMPLETE_ACTIVITY_STEPS.FORM
      this.activity = this.resolveActiveActivity()
      this.adjustedDeliveryLoads = []
      this.$refs.deliveryLoadComponent?.resetInitialPrefetchState()
      this.formErrors = {}
      this.customerNotes = this.isCustomerNotesRequired ? null : []
      this.fullObject = {
        customerNotes: this.isCustomerNotesRequired ? null : [],
      }
      this.attachments = []
      const data = {
        completion_source: 'web',
        queues: [
          {
            type: 'delete_attachment',
            point_id: this.point.id,
            activity_ids: this.pointActivities.map((a) => a.id),
          },
        ],
      }
      await FleetTaskActivity.bulkActivityCompletion(this.fleetTaskId, data)
      this.hasDeliveryLoadErrors = false
      this.convertedDeliveries = []
      this.isLoading = false
    },
    async handleModalCancel() {
      this.activeStep = BULK_COMPLETE_ACTIVITY_STEPS.FORM
      this.$refs.deliveryLoadComponent?.resetInitialPrefetchState()
      this.$bvModal.hide(`mc-modal-bulk-complete-${this.point.id}`)
    },
    filterUnchangedData(newValue, oldValue) {
      const keys = new Set([...Object.keys(newValue), ...Object.keys(oldValue)])
      const _newValue = {}

      keys.forEach((key) => {
        // if both value is falsy
        if (!oldValue[key] && !newValue[key]) return

        // if oldValue has same value with newValue
        if (oldValue[key] == newValue[key]) return

        // if the oldValue is 'Invalid date' and newValue is null
        if (oldValue[key] === 'Invalid date' && newValue[key] === null) return

        _newValue[key] = newValue[key]
      })

      const entries = Object.entries(_newValue)
      const hasChanged = entries.length > 0 && entries.every((value) => value)
      return [_newValue, hasChanged]
    },
    resetErrors() {
      this.invalidFormValues = {}
      this.formErrors = {}
    },
    assignErrors(errors) {
      this.resetErrors()

      const fieldErrorKeys = Object.keys(errors)

      fieldErrorKeys.forEach((fieldKey) => {
        this.$set(this.formErrors, fieldKey, apiErrorMappings[fieldKey][errors[fieldKey][0]])
        this.$set(this.invalidFormValues, fieldKey, this.formValues[fieldKey])
      })
    },
    async handleUpdate() {
      this.isLoading = true
      const deliveryLoadComponent = this.$refs['deliveryLoadComponent']

      if (!deliveryLoadComponent) {
        this.isLoading = false
        return
      }

      const data = this.adjustedDeliveryLoads

      const res = await FleetTaskActivity.adjustLoad(
        this.fleetTaskId,
        this.point.id,
        this.activity.id,
        data
      )

      if (res.errors_v2) {
        const hasErrorReasonNull = res.errors_v2.issues.some(
          (issue) =>
            issue.code ===
            'FLEET_TASK.FLEET_TASK_DELIVERY_LOAD_ADJUSTMENT_INVALID_REASON_CANNOT_BE_NULL'
        )

        if (hasErrorReasonNull) {
          const loadsWithMissingReasons = this.adjustedDeliveryLoads.filter((load) => !load.reason)
          deliveryLoadComponent.highlightErrorFields(loadsWithMissingReasons)

          this.$mcToast.error({
            component: 'McToastDynamicBody',
            props: {
              contents: [
                {
                  html: this.$t('fleetTask.bulkCompleteActivityModal.toast.loadReasonRequired'),
                },
              ],
            },
          })
          this.isLoading = false
          return
        }

        this.isLoading = false
        return
      }

      this.isLoading = false
      this.$bvModal.hide(`mc-modal-bulk-complete-${this.point.id}`)
      this.$mcToast.success({
        component: 'McToastDynamicBody',
        props: {
          contents: [
            {
              html: this.$t('fleetTask.bulkCompleteActivityModal.toast.loadQuantityUpdated', {
                activityLabel: this.activityLabelLower,
                doNumber: this.activity.delivery_order?.number,
              }),
            },
          ],
        },
      })

      if (this.adjustedDeliveryLoads.length > 0) {
        this.track('Delivery Load Adjustment after Transition', {
          'Fleet Order Number': this.fleetTaskDetail.number,
          'Activity Name': this.isPickUp ? 'Ambil' : 'Kirim',
        })
      }

      this.adjustedDeliveryLoads = []
      EventBus.$emit('refetch-sequence')
    },
  },
}
</script>

<style scoped lang="scss">
.label__do-number {
  color: #344264;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  margin: 0;
}

.mc-alert-container {
  .mc-alert {
    padding: 2px;
    .mc-alert-icon {
      &:before {
        font-size: 14px;
      }
    }
  }
}

.icon-circle-blue {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #007bff; /* Blue background color */
  margin-right: 8px;

  .mc-fa {
    color: white; /* Make the icon white for better contrast */
    &:before {
      font-size: 20px;
    }
  }
}

:deep(.note-textarea) {
  textarea {
    height: 75px;
    min-height: 75px;
    resize: vertical;
  }
}

.bulk-complete-date-field {
  :deep(.mx-datepicker) {
    width: 100%;
    display: block;
  }

  :deep(.mx-input) {
    width: 100%;
  }
}
</style>
